import { UserRole, XeroSyncStatus } from '@avanzu/types';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useState } from 'react';
import PaperSection from '../../components/PaperSection';
import { useAuth } from '../../contexts/AuthContext';
import ContentWrapper from '../../layout/ContentWrapper';
import {
  XeroConnectionWithStatus,
  disconnectXero,
  getOAuthResponseSuccess,
  isXeroOAuthResponse,
  loadXeroConnectionStatus,
  openXeroConnectPopup,
} from '../../services/xeroConnect';
import { PermissionsTable } from './components/PermissionsTable';
import SelfInvoiceConfig from './components/SelfInvoiceConfig';
import AccountTypesConfig from './components/AccountTypesConfig';
import { AccountType } from '@avanzu/types';

export const XeroRoles = [UserRole.AVANZU, UserRole.OWNER];

export const Xero: React.FC = () => {
  const { currentOrganisation, refreshUserOrganisationState } = useAuth();
  const [connectionInfo, setConnectionInfo] = useState<XeroConnectionWithStatus | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  const [hasProcessedMessage, setHasProcessedMessage] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  // Load connection status on mount
  useEffect(() => {
    loadConnectionStatus();
  }, [currentOrganisation?.id]);

  // Handle OAuth popup messages
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (!isXeroOAuthResponse(event)) return;
      if (hasProcessedMessage) return;

      setHasProcessedMessage(true);

      if (getOAuthResponseSuccess(event)) {
        enqueueSnackbar('Xero organisation successfully connected', { variant: 'success' });
        setIsConnecting(false);
        await loadConnectionStatus();
      } else {
        enqueueSnackbar('Failed to connect Xero organisation. Please try again.', {
          variant: 'error',
        });
        setIsConnecting(false);
      }

      refreshUserOrganisationState();
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [hasProcessedMessage]);

  const loadConnectionStatus = async () => {
    if (!currentOrganisation?.id) return;

    const connection = await loadXeroConnectionStatus(currentOrganisation.id);
    setConnectionInfo(connection);
  };

  const clearMessages = useCallback(() => {
    setError(null);
  }, []);

  const handleConnect = () => {
    if (!currentOrganisation?.id) return;

    setIsConnecting(true);
    setHasProcessedMessage(false);
    clearMessages();

    const popup = openXeroConnectPopup('/settings/integrations');

    if (!popup || popup.closed) {
      setError('Please allow popups for this site to connect your Xero organisation.');
      setIsConnecting(false);
      return;
    }

    const checkPopup = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkPopup);
        setTimeout(() => {
          if (isConnecting && !hasProcessedMessage) {
            enqueueSnackbar('Connection cancelled or failed. Please try again.', {
              variant: 'error',
            });
            setIsConnecting(false);
          }
        }, 500);
      }
    }, 1000);
  };

  const handleDisconnect = async () => {
    if (!currentOrganisation?.id) return;

    setIsDisconnecting(true);
    clearMessages();

    const success = await disconnectXero(currentOrganisation.id);

    if (success) {
      enqueueSnackbar('Xero integration successfully disconnected', { variant: 'warning' });
      await loadConnectionStatus();
    } else {
      setError('Failed to disconnect Xero integration');
      enqueueSnackbar('Failed to disconnect Xero integration. Please try again.', {
        variant: 'error',
      });
    }

    setIsDisconnecting(false);
    setShowDisconnectDialog(false);
    refreshUserOrganisationState();
  };

  if (!currentOrganisation) {
    return (
      <ContentWrapper breadcrumb>
        <PaperSection title="Xero Integration">
          <Alert severity="warning">
            Please select an organisation to manage Xero integration.
          </Alert>
        </PaperSection>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper breadcrumb>
      <PaperSection title="Xero Integration">
        <Stack spacing={3}>
          <Typography variant="body1" color="text.secondary">
            Connect your organisation to Xero to automatically import accounts, transactions,
            employees, payslips and assets.
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Avanzu only requests READ ONLY access to Xero. We will never modify or write data to
            your Xero organisation.
          </Alert>

          {/* Error and Success Messages */}
          {error && (
            <Alert severity="error" onClose={clearMessages} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Connection Card */}
          <Card
            variant="outlined"
            sx={{
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: 2,
                borderColor: 'primary.main',
              },
              ...(connectionInfo?.isConnected && {
                borderColor: 'success.main',
                boxShadow: '0 0 0 1px rgba(25, 118, 210, 0.5)',
              }),
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                {/* Xero Logo/Icon */}
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    bgcolor: connectionInfo?.isConnected ? 'success.light' : 'grey.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img src="/images/xero-logo.svg" alt="Xero" style={{ width: 48, height: 48 }} />
                </Box>

                {/* Connection Info */}
                <Box sx={{ flex: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                    <Typography variant="h6">Xero</Typography>
                  </Stack>

                  <Typography variant="body2" color="text.secondary">
                    {connectionInfo?.isConnected ? (
                      <>
                        Connected to <strong>{connectionInfo?.xeroTenantName}</strong>
                        {connectionInfo?.xeroConnectedAt && (
                          <> on {new Date(connectionInfo.xeroConnectedAt).toLocaleDateString()}</>
                        )}
                      </>
                    ) : (
                      'Connect your Xero organisation to import transactions and manage costs'
                    )}
                  </Typography>

                  {/* Additional status info */}
                  {connectionInfo?.isConnected && (
                    <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                      {connectionInfo?.xeroSyncStatus && (
                        <Chip
                          label={`Sync: ${connectionInfo?.xeroSyncStatus}`}
                          size="small"
                          variant="outlined"
                          color={
                            connectionInfo?.xeroSyncStatus === XeroSyncStatus.COMPLETE
                              ? 'success'
                              : 'default'
                          }
                        />
                      )}
                      {connectionInfo?.xeroLastSyncAt && (
                        <Typography variant="caption" color="text.secondary">
                          Last sync: {new Date(connectionInfo.xeroLastSyncAt).toLocaleDateString()}
                        </Typography>
                      )}
                    </Stack>
                  )}
                </Box>

                {connectionInfo?.isConnected && (
                  <Chip
                    icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                    label="Connected"
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                )}

                {/* Action Button */}
                <Box>
                  {connectionInfo?.isConnected ? (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      disabled={isDisconnecting}
                      startIcon={
                        isDisconnecting ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <LinkOffIcon />
                        )
                      }
                      onClick={() => setShowDisconnectDialog(true)}
                      sx={{ minWidth: 100 }}
                    >
                      {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      disabled={isConnecting}
                      onClick={handleConnect}
                      startIcon={
                        isConnecting ? <CircularProgress size={16} color="inherit" /> : undefined
                      }
                      sx={{ minWidth: 100 }}
                    >
                      {isConnecting ? 'Connecting...' : 'Connect'}
                    </Button>
                  )}
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {connectionInfo?.isConnected && (
            <SelfInvoiceConfig
              organisationId={currentOrganisation.id}
              initialEnabled={!!currentOrganisation.selfInvoicesEnabled}
              initialContactId={currentOrganisation.selfXeroContactId ?? null}
              disabled={isConnecting || isDisconnecting}
              onSaved={() => refreshUserOrganisationState()}
            />
          )}

          <AccountTypesConfig
            organisationId={currentOrganisation.id}
            initialAllowed={
              currentOrganisation.allowedAccountTypes && currentOrganisation.allowedAccountTypes.length
                ? currentOrganisation.allowedAccountTypes
                : [AccountType.EXPENSE]
            }
            disabled={isConnecting || isDisconnecting}
            onSaved={() => refreshUserOrganisationState()}
          />
        </Stack>
      </PaperSection>

      {/* Disconnect Confirmation Dialog */}
      <Dialog
        open={showDisconnectDialog}
        onClose={() => setShowDisconnectDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <ErrorOutlineIcon color="warning" />
            <Typography variant="h6">Disconnect Xero Integration?</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to disconnect your Xero integration for{' '}
            <strong>{currentOrganisation.name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This will remove access to Xero data and stop automatic transaction imports. You can
            reconnect at any time, but any pending transaction mappings may be lost.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDisconnectDialog(false)} disabled={isDisconnecting}>
            Cancel
          </Button>
          <Button
            onClick={handleDisconnect}
            color="error"
            variant="contained"
            disabled={isDisconnecting}
            startIcon={
              isDisconnecting ? <CircularProgress size={16} color="inherit" /> : <LinkOffIcon />
            }
          >
            {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
          </Button>
        </DialogActions>
      </Dialog>
    </ContentWrapper>
  );
};

export default Xero;
